/**
 * Progressive enhancement for campaign links that add a product without
 * leaving the current page. The native theme cart drawer remains the cart
 * shell and receives the same bundled section payload as a normal product
 * form submission.
 */

const ATC_BUTTON_SELECTOR =
  '[data-atc-submit], [data-cart-template-checkout], buy-buttons button[type="submit"]';
const CART_PERMALINK_RE = /\/cart\/(\d+):(\d+)/;

function setAtcLoading(loading) {
  document.querySelectorAll(ATC_BUTTON_SELECTOR).forEach((button) => {
    if (loading) {
      button.setAttribute('aria-busy', 'true');
      button.setAttribute('disabled', 'disabled');
    } else {
      button.removeAttribute('aria-busy');
      button.removeAttribute('disabled');
    }
  });
}

async function quickAddFromLink(variantId, quantity) {
  const root = window.Shopify?.routes?.root || '/';
  const sections = ['variant-added'];

  document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
    bubbles: true,
    detail: { sections },
  }));

  const addResponse = await fetch(`${root}cart/add.js`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
    body: JSON.stringify({
      items: [{ id: Number(variantId), quantity: Number(quantity) }],
      sections,
      sections_url: window.location.pathname,
    }),
    credentials: 'same-origin',
  });
  const addJson = await addResponse.json();

  if (!addResponse.ok) {
    document.dispatchEvent(new CustomEvent('cart:error', {
      detail: { error: addJson.description },
    }));
    throw new Error(addJson.description || 'cart/add failed');
  }

  const cart = await fetch(`${root}cart.js`, { credentials: 'same-origin' }).then((response) => response.json());
  cart.sections = addJson.sections || {};

  document.documentElement.dispatchEvent(new CustomEvent('variant:add', {
    bubbles: true,
    detail: { items: addJson.items || [addJson], cart },
  }));
  document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
    bubbles: true,
    detail: { baseEvent: 'variant:add', cart },
  }));
}

function onQuickAddClick(event) {
  if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
    return;
  }

  const link = event.target.closest('a[href]');
  if (!link) return;

  const match = link.getAttribute('href').match(CART_PERMALINK_RE);
  if (!match) return;

  const variantId = match[1];
  const quantity = parseInt(match[2], 10) || 1;

  event.preventDefault();
  setAtcLoading(true);
  quickAddFromLink(variantId, quantity)
    .catch(() => {
      window.location.href = link.href;
    })
    .finally(() => setAtcLoading(false));
}

document.addEventListener('click', onQuickAddClick, true);

/**
 * Shareable add-to-cart query parameters:
 *   ?add=43410582372558
 *   ?add=43410582372558:2
 *   ?add=gut-relief&qty=2
 */
function readAddParam() {
  const params = new URLSearchParams(window.location.search);
  const raw = params.get('add') || params.get('atc');
  if (!raw) return null;

  const [tokenPart, inlineQuantity] = raw.split(':');
  const token = (tokenPart || '').trim();
  if (!token) return null;

  const quantity = parseInt(params.get('qty') || inlineQuantity, 10) || 1;
  return { token, quantity };
}

function stripAddParam() {
  const url = new URL(window.location.href);
  ['add', 'atc', 'qty'].forEach((key) => url.searchParams.delete(key));
  window.history.replaceState({}, document.title, url.pathname + url.search + url.hash);
}

async function resolveVariantId(token) {
  if (/^\d+$/.test(token)) return token;

  const root = window.Shopify?.routes?.root || '/';
  const response = await fetch(`${root}products/${encodeURIComponent(token)}.js`, { credentials: 'same-origin' });
  if (!response.ok) throw new Error(`Unknown product handle: ${token}`);

  const product = await response.json();
  const variant = (product.variants || []).find((item) => item.available) || (product.variants || [])[0];
  if (!variant) throw new Error(`No variants for handle: ${token}`);

  return String(variant.id);
}

async function autoAddFromUrl() {
  const parsed = readAddParam();
  if (!parsed) return;

  stripAddParam();
  setAtcLoading(true);

  try {
    const variantId = await resolveVariantId(parsed.token);
    await quickAddFromLink(variantId, parsed.quantity);
  } finally {
    setAtcLoading(false);
  }
}

autoAddFromUrl().catch(() => {});
